import { useState } from 'react'
import {Download, Minus, Plus, RotateCw, X, ZoomIn, ZoomOut} from 'lucide-react'

interface DocumentoViewerModalProps {
  url: string
  nomeArquivo: string
  onClose: () => void
}

function getExtensao(nome: string, url: string): string {
  const alvo = nome || url || ''
  const partes = alvo.split('.')
  return partes.length > 1 ? partes.pop()!.toLowerCase().split('?')[0] : ''
}

const ZOOM_MIN = 50
const ZOOM_MAX = 300
const ZOOM_STEP = 25

function DocumentoViewerModal({ url, nomeArquivo, onClose }: DocumentoViewerModalProps) {
  const ext = getExtensao(nomeArquivo, url)
  const isImagem = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)
  const isPdf = ext === 'pdf'

  const [zoom, setZoom] = useState(100)
  const [rotacao, setRotacao] = useState(0)

  const diminuirZoom = () => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))
  const aumentarZoom = () => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))
  const resetarZoom = () => { setZoom(100); setRotacao(0) }
  const girar = () => setRotacao(r => (r + 90) % 360)

  // Para PDFs, usamos os parâmetros nativos do visualizador do navegador
  // (zoom e página) via querystring do próprio PDF viewer embutido no iframe.
  const pdfSrc = isPdf ? `${url}#zoom=${zoom}&navpanes=0&toolbar=1` : url

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-[900px] max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#E7E9EC] shrink-0 gap-3">
          <p className="text-[13.5px] font-medium text-[#12141A] truncate pr-4">{nomeArquivo || 'Documento'}</p>
          <div className="flex items-center gap-3 shrink-0">
            {(isImagem || isPdf) && (
              <div className="flex items-center gap-1 bg-[#F1F2F4] rounded-lg p-1">
                <button
                  onClick={diminuirZoom}
                  disabled={zoom <= ZOOM_MIN}
                  className="p-1.5 rounded-md text-[#4B5563] hover:bg-white disabled:opacity-40"
                  title="Diminuir zoom"
                >
                  <ZoomOut className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
                <button
                  onClick={resetarZoom}
                  className="px-2 text-[11.5px] font-medium text-[#4B5563] hover:text-[#12141A] min-w-[42px] text-center"
                  title="Repor zoom"
                >
                  {zoom}
                  %
                </button>
                <button
                  onClick={aumentarZoom}
                  disabled={zoom >= ZOOM_MAX}
                  className="p-1.5 rounded-md text-[#4B5563] hover:bg-white disabled:opacity-40"
                  title="Aumentar zoom"
                >
                  <ZoomIn className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
                {isImagem && (
                  <button
                    onClick={girar}
                    className="p-1.5 rounded-md text-[#4B5563] hover:bg-white"
                    title="Girar imagem"
                  >
                    <RotateCw className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                )}
              </div>
            )}

            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="flex items-center gap-1.5 text-[12.5px] font-medium text-[#1E4FBF] hover:text-[#1A44A6] whitespace-nowrap"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              Baixar
            </a>
            <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#12141A]">
              <X className="w-5 h-5" strokeWidth={1.75} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-[#F7F8FA] flex items-center justify-center min-h-[300px]">
          {isImagem
            ? (
                <img
                  src={url}
                  alt={nomeArquivo}
                  className="max-w-full max-h-[75vh] object-contain transition-transform duration-150"
                  style={{ transform: `scale(${zoom / 100}) rotate(${rotacao}deg)` }}
                />
              )
            : isPdf
              ? (
                  <iframe
                    key={pdfSrc}
                    src={pdfSrc}
                    title={nomeArquivo}
                    className="w-full h-[78vh] bg-white"
                  />
                )
              : (
                  <div className="flex flex-col items-center gap-3 py-16 px-6 text-center">
                    <p className="text-[13.5px] text-[#6B7280]">
                      Pré-visualização não disponível para este tipo de arquivo.
                    </p>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E4FBF] hover:bg-[#1A44A6] text-white text-[13px] font-medium"
                    >
                      <Download className="w-4 h-4" strokeWidth={1.75} />
                      Baixar arquivo
                    </a>
                  </div>
                )}
        </div>

        {isPdf && (
          <div className="flex items-center justify-center gap-2 px-5 py-2.5 border-t border-[#E7E9EC] shrink-0 text-[11.5px] text-[#9CA3AF]">
            <Plus className="w-3 h-3 rotate-45" strokeWidth={2} />
            Use os controlos de zoom acima ou os atalhos do visualizador de PDF do navegador para navegar entre páginas.
            <Minus className="w-3 h-3 rotate-45" strokeWidth={2} />
          </div>
        )}
      </div>
    </div>
  )
}

export default DocumentoViewerModal
